import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Typography, Table, Tag, Spin } from 'antd';
import {
  ShopOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  StarOutlined,
} from '@ant-design/icons';
import StatCard from '../components/StatCard';
import MapSection from '../components/MapSection';
import ProviderDetailModal from '../components/ProviderDetailModal';
import useBookingStore from '../store/bookingStore';
import useProviderStore from '../store/providerStore';
import useServiceStore from '../store/serviceStore';
import useReportStore from '../store/reportStore';
import usePaymentStore from '../store/paymentStore';
import useReviewStore from '../store/reviewStore';
import { availabilitySlotsAPI } from '../api/services';
import 'leaflet/dist/leaflet.css';

const { Title } = Typography;

const statusColors = {
  pending: 'orange',
  confirmed: 'blue',
  cancelled: 'red',
  completed: 'green',
  approved: 'green',
  rejected: 'red',
  initiated: 'default',
  paid: 'green',
  failed: 'red',
  refunded: 'purple',
};

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const { bookings, fetchBookings } = useBookingStore();
  const { providers, fetchProviders } = useProviderStore();
  const { services, fetchServices } = useServiceStore();
  const { reports, fetchReports } = useReportStore();
  const { payments, fetchPayments } = usePaymentStore();
  const { reviews, fetchReviews } = useReviewStore();

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      const [,,,,,, slotsRes] = await Promise.allSettled([
        fetchBookings({ per_page: 100 }),
        fetchProviders({ per_page: 100 }),
        fetchServices({ per_page: 100 }),
        fetchReports({ per_page: 100 }),
        fetchPayments({ per_page: 100 }),
        fetchReviews({ per_page: 100 }),
        availabilitySlotsAPI.getAll({ per_page: 500 }),
      ]);
      if (slotsRes?.status === 'fulfilled') {
        const sData = slotsRes.value?.data?.data || slotsRes.value?.data;
        setAvailabilitySlots(Array.isArray(sData) ? sData : sData?.data || []);
      }
      setLoading(false);
    };
    loadAll();
  }, [fetchBookings, fetchProviders, fetchServices, fetchReports, fetchPayments, fetchReviews]);

  const bookingColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Xizmat', dataIndex: 'service_id', key: 'service_id' },
    {
      title: 'Summa',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (v) => `${Number(v || 0).toLocaleString()} UZS`,
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColors[s]}>{s?.toUpperCase()}</Tag>,
    },
    {
      title: 'Sana',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (d) => d ? new Date(d).toLocaleDateString('uz-UZ') : '-',
    },
  ];

  const reportColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Xizmat nomi', dataIndex: 'service_name', key: 'service_name', ellipsis: true },
    { title: 'Manzil', dataIndex: 'address', key: 'address', ellipsis: true },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={statusColors[s]}>{s?.toUpperCase()}</Tag>,
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>Boshqaruv paneli</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <StatCard
            title="Provayderlar"
            value={providers?.length || 0}
            icon={<ShopOutlined />}
            color="#1677ff"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <StatCard
            title="Xizmatlar"
            value={services?.length || 0}
            icon={<AppstoreOutlined />}
            color="#722ed1"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <StatCard
            title="Buyurtmalar"
            value={bookings?.length || 0}
            icon={<CalendarOutlined />}
            color="#13c2c2"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <StatCard
            title="To'lovlar"
            value={payments?.length || 0}
            icon={<DollarOutlined />}
            color="#52c41a"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <StatCard
            title="Hisobotlar"
            value={reports?.length || 0}
            icon={<FileTextOutlined />}
            color="#fa8c16"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={8} xl={4}>
          <StatCard
            title="Sharhlar"
            value={reviews?.length || 0}
            icon={<StarOutlined />}
            color="#eb2f96"
            loading={loading}
          />
        </Col>
      </Row>

      <Card
        title="Xizmat ko'rsatuvchilar xaritasi"
        style={{ borderRadius: 12, marginBottom: 16 }}
        styles={{ header: { borderBottom: '1px solid #f0f0f0' }, body: { padding: 0 } }}
      >
        <MapSection
          providers={providers}
          services={services}
          availabilitySlots={availabilitySlots}
          onMarkerClick={(prov) => {
            setSelectedProvider(prov);
            setModalOpen(true);
          }}
        />
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <Card
            title="So'nggi buyurtmalar"
            style={{ borderRadius: 12 }}
            styles={{ header: { borderBottom: '1px solid #f0f0f0' } }}
          >
            <Table
              dataSource={bookings?.slice(0, 5)}
              columns={bookingColumns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 500 }}
            />
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <Card
            title="So'nggi hisobotlar"
            style={{ borderRadius: 12 }}
            styles={{ header: { borderBottom: '1px solid #f0f0f0' } }}
          >
            <Table
              dataSource={reports?.slice(0, 5)}
              columns={reportColumns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: 400 }}
            />
          </Card>
        </Col>
      </Row>

      <ProviderDetailModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedProvider(null); }}
        provider={selectedProvider}
        allServices={services}
        allBookings={bookings}
        allPayments={payments}
        allReviews={reviews}
        allSlots={availabilitySlots}
      />
    </div>
  );
};

export default DashboardPage;
